"use client";

import { useState, type ReactNode } from "react";
import { Play } from "lucide-react";
import { ChipRow } from "@/components/brand";
import { COSTS } from "@/lib/product-facts";
import { cn } from "@/utils/ui";

/* Small example sketches of each stage. They show the shape of the screen, not real output. */

const snap = "relative min-h-[15rem] rounded-lg border border-border-strong bg-background p-4 text-xs";

function Cap({ children = "Example" }: { children?: string }) {
	return <span className="absolute bottom-1.5 left-3 text-[10px] text-muted-foreground">{children}</span>;
}

function Bar({ label, value }: { label: string; value: number }) {
	return (
		<div className="my-2 grid grid-cols-[6rem_1fr_2rem] items-center gap-2">
			<span>{label}</span>
			<span className="h-1.5 rounded-full bg-muted"><span className="block h-full rounded-full bg-primary" style={{ width: `${value * 10}%` }} /></span>
			<span className="text-right tabular-nums text-muted-foreground">{value.toFixed(1)}</span>
		</div>
	);
}

const Lines = ({ widths }: { widths: string[] }) => (
	<div className="mt-3">{widths.map((w, i) => <span key={i} className="my-2 block h-1.5 rounded bg-muted" style={{ width: w }} />)}</div>
);

function ScriptSnap() {
	return (
		<div className={snap}>
			<Bar label="Hook" value={9.2} /><Bar label="Clarity" value={8.6} /><Bar label="Pacing" value={8.4} /><Bar label="Emotional pull" value={8.8} /><Bar label="Virality" value={8.1} />
			<Lines widths={["96%", "88%", "92%", "60%"]} /><Cap />
		</div>
	);
}

function VoiceSnap() {
	const rows = [["Narrator", "Warm · English", true], ["Oliver", "Playful · Hindi", false], ["His owner", "Calm · Hindi", false]] as const;
	return (
		<div className={snap}>
			{rows.map(([n, d, on]) => (
				<div key={n} className={cn("my-2 flex items-center gap-2.5 rounded-lg border bg-card px-2.5 py-2", on ? "border-primary" : "border-border")}>
					<span className="grid size-5 place-items-center rounded-full bg-secondary text-secondary-foreground"><Play className="size-2.5 fill-current" aria-hidden /></span>
					<b className="font-medium">{n}</b><em className="ml-auto text-[11px] not-italic text-muted-foreground">{d}</em>
				</div>
			))}
			<div className="mt-3 flex flex-wrap gap-1.5">
				{["Curiosity", "Awe", "Tension", "Warmth"].map((c) => (
					<span key={c} className={cn("rounded-full border px-2.5 py-0.5", c === "Awe" ? "border-primary bg-secondary text-secondary-foreground" : "border-border-strong")}>{c}</span>
				))}
			</div>
			<Cap />
		</div>
	);
}

function ScenesSnap() {
	const tiles = [["/marketing/demo-whale.webp", "9.1", "50% 30%"], ["/marketing/demo-rabbit.webp", "8.7", "30% 50%"], ["/marketing/demo-whale.webp", "9.0", "50% 70%"]];
	return (
		<div className={snap}>
			<div className="grid grid-cols-3 gap-2">
				{tiles.map(([src, score, pos], i) => (
					<div key={i} className="relative aspect-[9/13] rounded-md border border-border bg-cover" style={{ backgroundImage: `url(${src})`, backgroundPosition: pos }}>
						<b className="absolute right-1 top-1 rounded-full bg-black/65 px-1.5 text-[10px] text-white">{score}</b>
					</div>
				))}
			</div>
			<Lines widths={["90%", "70%"]} /><Cap />
		</div>
	);
}

function ClipsSnap() {
	return (
		<div className={snap}>
			<div className="relative aspect-video rounded-md border border-border bg-cover bg-center" style={{ backgroundImage: "url(/marketing/demo-rabbit.webp)" }}>
				<span className="absolute left-2 top-2 rounded-full bg-black/65 px-2 text-[10px] font-semibold text-white">480p</span>
				<span className="absolute inset-x-2.5 bottom-2.5 h-[3px] rounded bg-white/25"><span className="block h-full w-2/5 rounded bg-primary" /></span>
			</div>
			<Lines widths={["84%"]} /><Cap />
		</div>
	);
}

function StudioSnap() {
	const track = (name: string, bars: [number, number, boolean?][]) => (
		<div className="grid grid-cols-[3.9rem_1fr] items-center gap-2 text-[11px] text-muted-foreground">
			{name}
			<span className="relative block h-4 rounded bg-muted">
				{bars.map(([l, w, soft], i) => (
					<span key={i} className={cn("absolute inset-y-0 rounded", soft ? "border border-border-strong bg-muted" : "border border-primary bg-secondary")} style={{ left: `${l}%`, width: `${w}%` }} />
				))}
			</span>
		</div>
	);
	return (
		<div className={snap}>
			<div className="grid gap-2">
				{track("Video", [[0, 30], [31, 26], [58, 40]])}
				{track("Voice", [[2, 94]])}
				{track("Music", [[0, 100, true]])}
				{track("Subtitles", [[4, 20, true], [28, 22, true], [54, 20, true]])}
			</div>
			<div className="mt-4 flex justify-end"><span className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Export</span></div>
			<Cap />
		</div>
	);
}

type Stage = { id: string; tab: string; cost: string; title: string; body: string; decide: string; extra?: [string, string]; costLine: string; snap: ReactNode };

const STAGES: Stage[] = [
	{ id: "script", tab: "Script", cost: `${COSTS.script} cr`, title: "A script with a hook, a turn and a close", body: "It writes for the length you chose and scores it. If a score is low it shows a rewrite you can accept or ignore.", decide: "Write your own, or edit any line.", extra: ["Language", "Hindi, Spanish, English and more."], costLine: `About ${COSTS.script} credits per script`, snap: <ScriptSnap /> },
	{ id: "voice", tab: "Voice", cost: `~${COSTS.voice30s} cr`, title: "A voice for every character", body: "Each character gets a voice that fits. Pick an emotion for the delivery, then listen before you commit.", decide: "Swap any voice, change the emotion, regenerate.", costLine: `About ${COSTS.voice30s} credits for a 30-second voiceover`, snap: <VoiceSnap /> },
	{ id: "scenes", tab: "Scenes", cost: `${COSTS.keyframe} cr each`, title: "Every scene planned, then drawn", body: "The scene planner turns the script into a shot list. Each scene gets a keyframe image that is scored against the story beat it should show.", decide: "Edit the prompt, regenerate one scene, or upload your own image.", costLine: `${COSTS.keyframe} credits per scene image`, snap: <ScenesSnap /> },
	{ id: "clips", tab: "Clips", cost: `${COSTS.clipOmniPerSec} cr per second`, title: "Still images become moving clips", body: "Each approved image is animated into a short clip. You review it before the next one starts, so you never pay for a batch you did not want.", decide: "Write the motion, approve or retry each clip.", costLine: `${COSTS.clipOmniPerSec * 7} credits per clip`, snap: <ClipsSnap /> },
	{ id: "studio", tab: "Editor", cost: `${COSTS.assembly} cr`, title: "Finish it in a real editor", body: "Everything lands on a timeline. Trim, reorder, change the music, restyle the subtitles, add a hook overlay, then export.", decide: "Everything. It is your timeline.", extra: ["Bring yours", "Add your own clips, images and audio."], costLine: `${COSTS.assembly} credits for the final assembly`, snap: <StudioSnap /> },
];

export function PipelineExplorer() {
	const [active, setActive] = useState(STAGES[0].id);
	const stage = STAGES.find((s) => s.id === active) ?? STAGES[0];
	return (
		<div data-testid="pipeline-explorer" className="mt-12">
			<div role="tablist" aria-label="Pipeline stages" className="mb-3">
				<ChipRow>
					{STAGES.map((s) => (
						<button
							key={s.id}
							role="tab"
							type="button"
							id={`pipe-tab-${s.id}`}
							aria-selected={s.id === active}
							aria-controls="pipe-panel"
							data-testid={`pipe-${s.id}`}
							onClick={() => setActive(s.id)}
							className={cn(
								"flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-4 text-sm transition-colors sm:min-h-10",
								s.id === active ? "border-primary bg-secondary font-semibold text-secondary-foreground" : "border-border-strong hover:bg-accent",
							)}
						>
							{s.tab}<small className="text-xs opacity-75">{s.cost}</small>
						</button>
					))}
				</ChipRow>
			</div>
			<div id="pipe-panel" role="tabpanel" aria-labelledby={`pipe-tab-${stage.id}`} className="grid items-center gap-7 rounded-2xl border border-border bg-card p-5 sm:p-7 lg:grid-cols-[1.25fr_1fr] lg:gap-9">
				{stage.snap}
				<div>
					<h3 className="font-display text-2xl font-semibold leading-tight">{stage.title}</h3>
					<p className="mt-2 max-w-md text-muted-foreground">{stage.body}</p>
					<p className="mt-3 flex gap-2.5 text-sm"><b className="min-w-[5.6em] font-semibold text-primary">You decide</b><span>{stage.decide}</span></p>
					{stage.extra && <p className="mt-2 flex gap-2.5 text-sm"><b className="min-w-[5.6em] font-semibold text-primary">{stage.extra[0]}</b><span>{stage.extra[1]}</span></p>}
					<p className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground">{stage.costLine}</p>
				</div>
			</div>
			<p className="mt-3 text-sm text-muted-foreground">Credits shown are what each step costs today. You see the price before you start a step.</p>
		</div>
	);
}
